import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowLeft, AlertCircle, Check, KeyRound, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TennisForgotPassword = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('password');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successType, setSuccessType] = useState('');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/tennis/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSuccess(true);
        setSuccessType('password');
        toast.success(t('tennis.checkEmail'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotUsername = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/tennis/forgot-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });

      if (response.ok) {
        setSuccess(true);
        setSuccessType('username');
        toast.success(t('tennis.checkEmail'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="tennis-forgot-success" className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">{t('tennis.checkEmail')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            {successType === 'password' 
              ? t('tennis.passwordSentMessage')
              : t('tennis.usernameSentMessage')}
          </p>
          <div className="space-y-4">
            <Link to="/tennis/login">
              <Button className="w-full bg-green-500 text-white font-bold hover:bg-green-500/90">
                {t('tennis.goToLogin')}
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => {
                setSuccess(false);
                setEmail('');
                setPhone('');
              }}
              className="w-full border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {t('tennis.tryAgain')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="tennis-forgot-page" className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Back button */}
        <Link to="/tennis/login" className="inline-flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('tennis.backToLogin')}
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('tennis.forgotPasswordTitle')}</h1>
          <p className="text-slate-500 mt-2">{t('tennis.helpYouAccess')}</p>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full flex gap-2 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg mb-6">
              <TabsTrigger 
                value="password"
                data-testid="forgot-tab-password"
                className="flex-1 data-[state=active]:bg-green-500 data-[state=active]:text-white text-slate-900 dark:text-slate-300 py-2 rounded-md font-medium transition-all"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {t('tennis.forgotPasswordTitle')}
              </TabsTrigger>
              <TabsTrigger 
                value="username"
                data-testid="forgot-tab-username"
                className="flex-1 data-[state=active]:bg-green-500 data-[state=active]:text-white text-slate-900 dark:text-slate-300 py-2 rounded-md font-medium transition-all"
              >
                <User className="w-4 h-4 mr-2" />
                {t('tennis.forgotUsernameTitle')}
              </TabsTrigger>
            </TabsList>

            {/* Forgot Password Tab */}
            <TabsContent value="password">
              <form onSubmit={handleForgotPassword}>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                  {t('tennis.enterEmail')}
                </p>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      data-testid="forgot-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                      placeholder="din@epost.no"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-password-submit"
                  className="w-full bg-green-500 text-white font-bold hover:bg-green-500/90"
                  disabled={loading}
                >
                  {loading ? t('tennis.sending') : t('tennis.sendNewPassword')}
                </Button>
              </form>
            </TabsContent>

            {/* Forgot Username Tab */}
            <TabsContent value="username">
              <form onSubmit={handleForgotUsername}>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                  {t('tennis.enterPhone')}
                </p>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.phone')}</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="tel"
                      data-testid="forgot-phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                      placeholder="+47 123 45 678"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-username-submit"
                  className="w-full bg-green-500 text-white font-bold hover:bg-green-500/90"
                  disabled={loading}
                >
                  {loading ? t('tennis.sending') : t('tennis.sendUsername')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Info box */}
        <div className="mt-6 bg-slate-100 dark:bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              <strong className="text-slate-900 dark:text-white">{t('tennis.tip')}:</strong> {t('tennis.checkSpam')}{' '}
              <a href="mailto:nickolas.paynter@veitvet-sportsklubb.no" className="text-green-500 hover:underline">
                nickolas.paynter@veitvet-sportsklubb.no
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TennisForgotPassword;
